import React, { FC } from 'react';
import styled from 'styled-components';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { media } from 'src/styles/designs';
import { StickyMenu } from 'src/components/common/StickyMenu';
import { useRectMeasure } from 'src/hooks/useRectMeasure';
import { ProductDTO } from 'src/models/pim';
import { Theme } from 'vendor/mods/styled-components';
import { dirRTL, nroseTheme, redTheme } from 'src/styles/theme';
import { ProductLogo } from 'src/modules/products/components/ProductLogo';
import { Heading } from 'src/components/common/Heading';
import { Paragraph } from 'src/components/common/typography/Text';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { PimImage } from 'src/components/common/PimImage';

const Needle = styled.div`
  color: ${({ theme }) => theme.text.palette.white};
  position: absolute;

  &::before {
    content: '';
    background-color: currentColor;
    position: absolute;
    top: -10px;
    left: -90px;
    width: 65px;
    height: 285px;
    clip-path: polygon(0 0, 40% 0, 100% 100%, 95% 100%);

    ${dirRTL} {
      left: auto;
      right: -90px;
      clip-path: polygon(60% 0, 100% 0, 5% 100%, 0 100%);
    }
  }

  ${media.w.lessThan.desktop} {
    display: none;
  }
`;

const Container = styled.section`
  color: ${({ theme }) => theme.text.palette.primary};
  background: ${({ theme }) => theme.gradients.primary};

  ${media.w.greaterThanOrEqual.desktop} {
    min-height: 574px;
  }
`;

const ContentContainer = styled.div`
  padding: 64px 0 52px;

  ${media.w.greaterThanOrEqual.desktop} {
    display: flex;
    justify-content: space-between;
    gap: 48px;
    max-width: 1080px;

    padding-left: 60px;
    ${media.w.lessThan.desktop} {
      padding-right: 60px;
      padding-left: 0;
    }
  }
`;

const Content = styled.div`
  position: relative;
  max-width: 360px;

  ${media.w.greaterThanOrEqual.desktop} {
    ${Heading}::before {
      content: none;
    }
  }
`;

const Highlight = styled(Paragraph)`
  font-weight: 500;
  margin: 32px 0 16px;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 427px;
  height: 424px;

  ${media.w.lessThan.desktop} {
    display: none;
  }
`;

const ImageInnerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: -92px;
`;

export const ProductPageProtocolsBannerSection: FC<ProductDTO> = (_product) => {
  const [bannerRef, height] = useRectMeasure();

  const product = useTranslateItem(_product);

  return (
    <Theme theme={product.code === 'nrose' ? nroseTheme : redTheme}>
      <Container ref={bannerRef}>
        <StickyMenu bannerHeight={height} />
        <ConstraintContainer>
          <ContentContainer>
            <Content>
              <Needle />
              <Heading>
                <ProductLogo size="big" code={product.code} />
              </Heading>
              <Paragraph>{product.tagline}</Paragraph>

              <Highlight>
                {product.function && <Markdown>{product.function}</Markdown>}
              </Highlight>
              <Paragraph>{product.description_short}</Paragraph>
            </Content>

            {product.image && (
              <ImageContainer>
                <ImageInnerContainer>
                  <PimImage
                    src={product.image.id}
                    layout="fill"
                    objectFit="cover"
                    sizes="100vw"
                  />
                </ImageInnerContainer>
              </ImageContainer>
            )}
          </ContentContainer>
        </ConstraintContainer>
      </Container>
    </Theme>
  );
};
